import { Box, Alert } from "@mui/material";
import React, { useMemo, useState } from "react";
import {
  useGetAllAbsenceBalancesQuery,
  useGetEmployeeAbsenceBalanceQuery,
} from "../../api/absenceBalancesApi";
import { useGetEmployeesQuery } from "../../api/employeesApi";
import { LoadingSpinner } from "../../components/common/LoadingSpinner";
import EmployeeBalancesList from "./components/EmployeeBalancesList";
import AdjustBalanceDialog from "./components/AdjustBalanceDialog";
import { EmployeeAbsenceBalanceDto } from "../../types/absence";
import { useInfiniteDataTableItems } from "../../hooks/useInfiniteDataTableItems";
import { DEFAULT_DATA_TABLE_PAGE_SIZE } from "../../constants/dataTable";
import type { SearchableColumn } from "../../components/common/SearchInputs";

const AdminBalancesPage: React.FC = () => {
  const [page, setPage] = useState(0);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<number | null>(null);
  const [employeeToAdjust, setEmployeeToAdjust] = useState<EmployeeAbsenceBalanceDto | null>(null);

  const {
    data: balancesData,
    isLoading,
    isFetching,
    error,
  } = useGetAllAbsenceBalancesQuery({
    pageNumber: page + 1,
    pageSize: DEFAULT_DATA_TABLE_PAGE_SIZE,
  });
  const { accumulatedItems: allBalances, resetAccumulatedItems } =
    useInfiniteDataTableItems<EmployeeAbsenceBalanceDto>(balancesData?.items, page);

  const {
    data: selectedEmployeeBalance,
    isLoading: isSelectedBalanceLoading,
    isFetching: isSelectedBalanceFetching,
  } = useGetEmployeeAbsenceBalanceQuery(selectedEmployeeId ?? 0, {
    skip: selectedEmployeeId === null,
  });

  const { data: employees = [] } = useGetEmployeesQuery();

  const searchableColumns = useMemo<SearchableColumn[]>(
    () => [
      {
        field: "employeeName",
        headerName: "Employee",
        searchable: true,
        searchType: "select",
        selectOptions: employees.map((employee) => ({
          value: employee.id,
          label: `${employee.firstName} ${employee.lastName}`,
        })),
      },
    ],
    [employees]
  );

  const isEmployeeFilterActive = selectedEmployeeId !== null;
  const balances = isEmployeeFilterActive
    ? selectedEmployeeBalance
      ? [selectedEmployeeBalance]
      : []
    : allBalances;
  const displayedTotalCount = isEmployeeFilterActive
    ? selectedEmployeeBalance
      ? 1
      : 0
    : balancesData?.totalCount;
  const displayedHasNextPage = isEmployeeFilterActive
    ? false
    : balancesData?.hasNextPage;
  const isTableLoading = isEmployeeFilterActive
    ? isSelectedBalanceLoading && balances.length === 0
    : isLoading && balances.length === 0;
  const isLoadingRows = isEmployeeFilterActive
    ? isSelectedBalanceFetching
    : isFetching && page === 0 && !balancesData;
  const isLoadingMore = !isEmployeeFilterActive && isFetching && balances.length > 0;

  const resetBalancesTable = () => {
    resetAccumulatedItems();
    setPage(0);
  };

  const handleSearchChange = (filters: Record<string, any>) => {
    const employeeId = filters.employeeName;
    setSelectedEmployeeId(
      employeeId !== null && employeeId !== undefined && employeeId !== ""
        ? Number(employeeId)
        : null
    );
    resetBalancesTable();
  };

  const handleAdjustBalance = (balance: EmployeeAbsenceBalanceDto) => {
    setEmployeeToAdjust(balance);
  };

  const handleAdjustSuccess = () => {
    resetBalancesTable();
    setEmployeeToAdjust(null);
  };

  const handleLoadMore = () => {
    if (!isEmployeeFilterActive && balancesData?.hasNextPage && !isFetching) {
      setPage((currentPage) => currentPage + 1);
    }
  };

  if (isLoading && !balancesData) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">
          Failed to load employee balances. Please try again later.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <EmployeeBalancesList
        balances={balances}
        isLoading={isTableLoading}
        onAdjustClick={handleAdjustBalance}
        totalCount={displayedTotalCount}
        hasNextPage={displayedHasNextPage}
        isLoadingMore={isLoadingMore}
        isLoadingRows={isLoadingRows}
        onLoadMore={isEmployeeFilterActive ? undefined : handleLoadMore}
        enableSearch
        searchableColumns={searchableColumns}
        onSearchChange={handleSearchChange}
      />

      {/* Adjust Balance Dialog */}
      <AdjustBalanceDialog
        open={!!employeeToAdjust}
        employee={employeeToAdjust}
        onClose={() => setEmployeeToAdjust(null)}
        onSuccess={handleAdjustSuccess}
      />
    </Box>
  );
};

export default AdminBalancesPage;

